import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, AlertCircle, Clock, Activity, Server, Database, Zap, TrendingUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatusPage() {
  const navigate = useNavigate();
  const [systemStatus, setSystemStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSystemStatus();
    // Refresh every 30 seconds
    const interval = setInterval(fetchSystemStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchSystemStatus = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/health`);
      setSystemStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch status:', error);
      setSystemStatus({ status: 'degraded', database: 'unknown' });
    } finally {
      setLoading(false);
    }
  };

  const services = [
    {
      name: 'API Gateway',
      status: 'operational',
      uptime: '99.98%',
      latency: '145ms',
      description: 'Core API endpoints'
    },
    {
      name: 'Identity Verification',
      status: 'operational',
      uptime: '99.95%',
      latency: '1.2s',
      description: 'NIN, BVN, Passport verification'
    },
    {
      name: 'Biometric Service',
      status: 'operational',
      uptime: '99.92%',
      latency: '890ms',
      description: 'Liveness detection & facial recognition'
    },
    {
      name: 'Address Verification',
      status: 'operational',
      uptime: '99.87%',
      latency: '18h',
      description: 'Field agent network'
    },
    {
      name: 'Database',
      status: systemStatus?.database === 'healthy' ? 'operational' : 'degraded',
      uptime: '99.99%',
      latency: '12ms',
      description: 'MongoDB cluster'
    },
    {
      name: 'Webhooks',
      status: 'operational',
      uptime: '99.94%',
      latency: '250ms',
      description: 'Event notifications'
    }
  ];

  const slas = [
    {
      metric: 'API Availability',
      target: '99.9%',
      current: '99.98%',
      status: 'exceeding'
    },
    {
      metric: 'ID Verification Speed',
      target: '< 2s',
      current: '1.2s',
      status: 'meeting'
    },
    {
      metric: 'Address Verification',
      target: '< 24h',
      current: '18h',
      status: 'meeting'
    },
    {
      metric: 'Support Response',
      target: '< 24h',
      current: '6h',
      status: 'exceeding'
    }
  ];

  const incidents = [
    {
      date: '2025-01-15',
      title: 'Scheduled Maintenance',
      status: 'resolved',
      duration: '30 minutes',
      description: 'Database optimization and indexing'
    },
    {
      date: '2025-01-10',
      title: 'Elevated API Latency',
      status: 'resolved',
      duration: '2 hours',
      description: 'Temporarily increased response times due to high traffic. Resolved by scaling infrastructure.'
    }
  ];

  const performanceMetrics = [
    { label: 'Total API Calls (30d)', value: '1.2M', icon: Activity, color: 'blue' },
    { label: 'Avg Response Time', value: '145ms', icon: Zap, color: 'yellow' },
    { label: 'Success Rate', value: '99.8%', icon: TrendingUp, color: 'green' },
    { label: 'Active Clients', value: '247', icon: Server, color: 'purple' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'operational':
      case 'healthy':
        return 'text-green-600';
      case 'degraded':
        return 'text-yellow-600';
      case 'outage':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'operational':
      case 'healthy':
        return 'bg-green-100';
      case 'degraded':
        return 'bg-yellow-100';
      case 'outage':
        return 'bg-red-100';
      default:
        return 'bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'operational':
      case 'healthy':
        return <CheckCircle size={20} className="text-green-600" />;
      case 'degraded':
        return <AlertCircle size={20} className="text-yellow-600" />;
      case 'outage':
        return <AlertCircle size={20} className="text-red-600" />;
      default:
        return <Clock size={20} className="text-gray-600" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5a738f] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading system status...</p>
        </div>
      </div>
    );
  }

  const overallStatus = systemStatus?.status === 'healthy' ? 'operational' : 'degraded';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <img 
              src="/hakicheck-logo.png" 
              alt="HakiCheck" 
              className="h-12 cursor-pointer"
              onClick={() => navigate('/')}
            />
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/pricing')}
                className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium"
              >
                Pricing
              </button>
              <button
                onClick={() => navigate('/auth')}
                className="px-6 py-2 bg-[#5a738f] hover:bg-[#4a5f7f] text-white font-medium rounded-lg transition-colors"
              >
                Sign In
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Status */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center justify-center mb-6">
            {getStatusIcon(overallStatus)}
            <span className={`ml-3 text-2xl font-bold ${getStatusColor(overallStatus)}`}>
              {overallStatus === 'operational' ? 'All Systems Operational' : 'System Degraded'}
            </span>
          </div>
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            HakiCheck System Status
          </h1>
          <p className="text-xl text-gray-600 mb-4">
            Real-time monitoring of all services and infrastructure
          </p>
          <p className="text-sm text-gray-500">
            Last updated: {new Date().toLocaleString()} (Auto-refreshes every 30s)
          </p>
        </div>
      </section>

      {/* Performance Metrics */}
      <section className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {performanceMetrics.map((metric) => {
              const Icon = metric.icon;
              const colorClasses = {
                blue: 'bg-blue-100 text-blue-600',
                yellow: 'bg-yellow-100 text-yellow-600',
                green: 'bg-green-100 text-green-600',
                purple: 'bg-purple-100 text-purple-600'
              };
              return (
                <div key={metric.label} className="bg-white rounded-xl shadow-md p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className={`p-3 rounded-lg ${colorClasses[metric.color]}`}>
                      <Icon size={24} />
                    </div>
                    <span className="text-3xl font-bold text-gray-900">{metric.value}</span>
                  </div>
                  <p className="text-gray-600 text-sm font-medium">{metric.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Status */}
      <section className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Service Status</h2>
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="divide-y divide-gray-200">
              {services.map((service) => (
                <div key={service.name} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                      {getStatusIcon(service.status)}
                      <div>
                        <h3 className="font-semibold text-gray-900">{service.name}</h3>
                        <p className="text-sm text-gray-600">{service.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-8">
                      <div className="text-right">
                        <div className="text-sm text-gray-600">Uptime</div>
                        <div className="font-semibold text-gray-900">{service.uptime}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-600">Latency</div>
                        <div className="font-semibold text-gray-900">{service.latency}</div>
                      </div>
                      <div className={`px-4 py-2 rounded-full ${getStatusBg(service.status)}`}>
                        <span className={`font-medium text-sm ${getStatusColor(service.status)}`}>
                          {service.status}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SLA Performance */}
      <section className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">SLA Performance</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {slas.map((sla) => (
              <div key={sla.metric} className="bg-white rounded-xl shadow-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900">{sla.metric}</h3>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    sla.status === 'exceeding' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {sla.status}
                  </span>
                </div>
                <div className="flex items-baseline gap-4">
                  <div>
                    <div className="text-sm text-gray-600">Target</div>
                    <div className="text-2xl font-bold text-gray-400">{sla.target}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Current</div>
                    <div className="text-2xl font-bold text-green-600">{sla.current}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              📊 <strong>SLA Guarantee:</strong> We guarantee 99.9% uptime for all paid plans with automatic credits for any downtime exceeding SLA thresholds.
            </p>
          </div>
        </div>
      </section>

      {/* Incident History */}
      <section className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Recent Incidents</h2>
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            {incidents.length === 0 ? (
              <div className="p-12 text-center">
                <CheckCircle size={48} className="mx-auto text-green-600 mb-4" />
                <p className="text-gray-600">No incidents in the last 30 days</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {incidents.map((incident, idx) => (
                  <div key={idx} className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {incident.status}
                          </span>
                          <span className="text-sm text-gray-600">{incident.date}</span>
                        </div>
                        <h3 className="font-semibold text-gray-900 mb-1">{incident.title}</h3>
                        <p className="text-sm text-gray-600">{incident.description}</p>
                      </div>
                      <div className="text-right ml-4">
                        <div className="text-sm text-gray-600">Duration</div>
                        <div className="font-semibold text-gray-900">{incident.duration}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Subscribe */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Stay Informed</h2>
          <p className="text-gray-600 mb-8">
            Subscribe to get real-time notifications about system status and planned maintenance
          </p>
          <div className="flex gap-2 max-w-md mx-auto">
            <input
              type="email"
              placeholder="your.email@company.com"
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a738f] focus:border-transparent"
            />
            <button className="px-6 py-3 bg-[#5a738f] hover:bg-[#4a5f7f] text-white font-medium rounded-lg transition-colors">
              Subscribe
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-400">© 2025 HakiCheck. Trust Infrastructure for Africa.</p>
        </div>
      </footer>
    </div>
  );
}

export default StatusPage;
